import {
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSponsors } from "../../hooks";

const PAGE_SIZE = 20;

export function Sponsors() {
  const navigate = useNavigate();
  const { sponsors, loading, error } = useSponsors();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const filteredSponsors = useMemo(() => {
    if (!searchQuery.trim()) return sponsors;
    const query = searchQuery.trim().toLowerCase();
    return sponsors.filter((sponsor) =>
      sponsor.name.toLowerCase().includes(query),
    );
  }, [sponsors, searchQuery]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredSponsors.length / PAGE_SIZE),
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const paginatedSponsors = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredSponsors.slice(start, start + PAGE_SIZE);
  }, [filteredSponsors, currentPage]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("ja-JP");
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[#ef4444]">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="w-full">
        <div className="mb-8 flex items-start justify-between">
          <div className="border-[var(--color-primary-500)] border-l-4 pl-5">
            <h1 className="mb-2 font-bold text-[32px] text-[var(--color-primary-500)]">
              スポンサー管理
            </h1>
            <p className="font-medium text-[var(--color-text-secondary)] text-sm">
              スポンサーを選択してターム管理を行います
            </p>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
            <div>
              <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
                スポンサー一覧
              </h2>
              <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
                {searchQuery
                  ? `${filteredSponsors.length}件 / ${sponsors.length}件のスポンサー`
                  : `${sponsors.length}件のスポンサーが登録されています`}
              </p>
            </div>
            <div className="relative">
              <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[#9ca3af]" />
              <input
                type="text"
                placeholder="スポンサー名で検索..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-60 rounded-lg border border-[var(--color-border-default)] py-2 pr-3 pl-9 text-sm outline-none"
              />
            </div>
          </div>
          <div className="p-0">
            {filteredSponsors.length === 0 ? (
              <div className="p-12 text-center">
                <Building2 className="mx-auto mb-4 h-12 w-12 text-[#9ca3af]" />
                <p className="text-[var(--color-text-secondary)]">
                  {searchQuery
                    ? "検索条件に一致するスポンサーがありません"
                    : "スポンサーが登録されていません"}
                </p>
              </div>
            ) : (
              <table className="w-full table-fixed border-collapse">
                <colgroup>
                  <col className="w-[35%]" />
                  <col className="w-[50%]" />
                  <col className="w-[15%]" />
                </colgroup>
                <thead>
                  <tr className="bg-[var(--color-bg-subtle-gray)]">
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      スポンサー名
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      説明
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      作成日
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSponsors.map((sponsor) => (
                    <tr
                      key={sponsor.sponsorId}
                      onClick={() =>
                        navigate(
                          `/employee_interview/sponsors/${sponsor.sponsorId}`,
                        )
                      }
                      className="cursor-pointer border-[var(--color-border-default)] border-b hover:bg-[var(--color-bg-subtle-gray)]"
                    >
                      <td className="px-6 py-4 font-medium text-[var(--color-primary-500)] text-sm">
                        {sponsor.name}
                      </td>
                      <td className="px-6 py-4 text-[var(--color-text-secondary)] text-sm">
                        {sponsor.description || "-"}
                      </td>
                      <td className="px-6 py-4 text-[var(--color-text-secondary)] text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {formatDate(sponsor.createdAt)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {filteredSponsors.length > PAGE_SIZE && (
              <div className="flex items-center justify-between border-[var(--color-border-default)] border-t px-6 py-4">
                <p className="text-[13px] text-[var(--color-text-secondary)]">
                  {(currentPage - 1) * PAGE_SIZE + 1} -{" "}
                  {Math.min(currentPage * PAGE_SIZE, filteredSponsors.length)} /{" "}
                  {filteredSponsors.length}件
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((page) => Math.max(1, page - 1))
                    }
                    disabled={currentPage === 1}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-[var(--color-border-default)] text-[var(--color-text-secondary)] enabled:hover:bg-[var(--color-bg-subtle-gray)] disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="前のページ"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-[13px] text-[var(--color-text-secondary)]">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((page) => Math.min(totalPages, page + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-[var(--color-border-default)] text-[var(--color-text-secondary)] enabled:hover:bg-[var(--color-bg-subtle-gray)] disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="次のページ"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
